import VideoControls from '@utils/videoControls';

/** Safety timeout (ms) — reveal content even if media fails to load. */
const REVEAL_TIMEOUT_MS = 5000;

/**
 * Handles the static primary hero section. Observes media load events
 * (image or video) and reveals the content overlay once the media is ready.
 * Falls back to revealing after a safety timeout.
 */
export default class StaticPrimaryHero {
  private moduleEl: HTMLElement;
  private revealed = false;

  constructor(context: HTMLElement) {
    this.moduleEl = context;
    this.init();
  }

  /**
   * Kicks off media observation, video controls init, and the safety timeout.
   */
  private init(): void {
    this.observeMedia();
    this.initVideoControls();

    setTimeout(() => this.reveal(), REVEAL_TIMEOUT_MS);
  }

  /**
   * Attaches load listeners to the first visible image or video inside the
   * hero media container. Immediately reveals if media is already loaded.
   */
  private observeMedia(): void {
    const mediaContainer = this.moduleEl.querySelector('.hero__media');
    if (!mediaContainer) {
      this.reveal();
      return;
    }

    const videoElement = mediaContainer.querySelector('video');
    if (videoElement) {
      if (videoElement.readyState >= 3) {
        this.reveal();
      } else {
        videoElement.addEventListener('canplaythrough', () => this.reveal(), {
          once: true,
        });
      }
      return;
    }

    const imageElement = mediaContainer.querySelector('img');
    if (imageElement) {
      if (imageElement.complete && imageElement.naturalWidth > 0) {
        this.reveal();
      } else {
        imageElement.addEventListener('load', () => this.reveal(), {
          once: true,
        });
        imageElement.addEventListener('error', () => this.reveal(), {
          once: true,
        });
      }
      return;
    }

    this.reveal();
  }

  /**
   * Adds the loaded class to the section root, triggering CSS transitions
   * for media fade-in and content overlay appearance.
   */
  private reveal(): void {
    if (this.revealed) return;
    this.revealed = true;
    this.moduleEl.classList.add('primaryHero--loaded');
  }

  /**
   * Initializes custom video play/pause controls if the controls element is
   * present and native controls are not in use.
   */
  private initVideoControls(): void {
    const videoControlsEl = this.moduleEl.querySelector<HTMLElement>(
      '.js--video-controls'
    );
    if (videoControlsEl) {
      new VideoControls(videoControlsEl, this.moduleEl);
    }
  }
}
